/**
 * Freshdesk Solutions tab - the synced knowledge base (categories, folders and
 * articles in `fd_solution_*`). Reads the cached aggregate (no live FD call) and
 * renders it through the reusable `IntegrationSectionTab` shell: one "Sync now"
 * (the `freshdesk_solutions` run) over inner sub-tabs Articles | Categories |
 * Folders. The Articles table also powers the exception-link picker.
 *
 * Cross-tab nav: the Categories/Folders count cells are react-router links that
 * switch the shell's `view` param and pre-apply a `category` / `folder` filter
 * (the same params the Category/Folder picker writes), so a count deep-links to a
 * pre-filtered table.
 */
import React from 'react'
import { Link } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined'

import { useDataFetching } from '../../../../../hooks/useDataFetching'
import {
  FreshdeskSolutionArticle,
  FreshdeskSolutionCategory,
  FreshdeskSolutionFolder,
  FreshdeskSolutions,
  integrationsApi,
} from '../../../../../services/admin/integrationsApi'
import CategoryFolderSelect, {
  CategoryFolderValue,
} from '../../../../../components/shared/freshdesk/CategoryFolderSelect'
import {
  categoryFolder,
  marketChip,
  openLink,
  statusChip,
} from '../../../../../components/shared/freshdesk/freshdeskArticleHelpers'
import SolutionsBrowser from '../../../../../components/shared/freshdesk/SolutionsBrowser'
import IntegrationSectionTab, {
  SectionTab,
} from '../shared/IntegrationSectionTab'

const KEY = 'freshdesk_solutions'

// A count cell that deep-links to another sub-tab with a filter pre-applied.
// Zero renders as plain text (nothing to drill into).
const countLink = (count: number, search: string) =>
  count > 0 ? (
    <Link component={RouterLink} to={{ search }} sx={{ fontWeight: 600 }}>
      {count}
    </Link>
  ) : (
    count
  )

// Read / write the shared Category|Folder URL filter (Articles tab honours both;
// `folder` wins over `category`).
const readCatFolder = (params: URLSearchParams): CategoryFolderValue => {
  const folder = params.get('folder')
  if (folder) return { type: 'folder', id: Number(folder) }
  const category = params.get('category')
  if (category) return { type: 'category', id: Number(category) }
  return null
}

const writeCatFolder = (
  params: URLSearchParams,
  value: CategoryFolderValue,
): URLSearchParams => {
  const next = new URLSearchParams(params)
  next.delete('folder')
  next.delete('category')
  if (value) next.set(value.type, String(value.id))
  return next
}

const FreshdeskSolutionsTab: React.FC = () => {
  const { data, loading, refetch } = useDataFetching<FreshdeskSolutions>({
    fetchFn: () => integrationsApi.getFreshdeskSolutions(),
    dependencies: [],
  })

  const articles = data?.articles ?? []
  const folders = data?.folders ?? []
  const categories = data?.categories ?? []

  const entities: SectionTab[] = [
    {
      key: 'browse',
      label: 'Browse',
      icon: <TravelExploreOutlinedIcon fontSize="small" />,
      render: () => <SolutionsBrowser mode="browse" solutions={data ?? undefined} />,
    },
    {
      key: 'articles',
      label: 'Articles',
      icon: <ArticleOutlinedIcon fontSize="small" />,
      rows: articles,
      rowKey: (a: FreshdeskSolutionArticle) => `${a.article_id}-${a.market}`,
      defaultSortField: 'title',
      getMarket: (a: FreshdeskSolutionArticle) => a.market,
      searchText: (a: FreshdeskSolutionArticle) =>
        `${a.title} ${a.folder ?? ''} ${a.category ?? ''}`,
      searchPlaceholder: 'Search title, folder or category',
      extraFilter: {
        render: (params, setParams, { market }) => (
          <CategoryFolderSelect
            categories={categories}
            folders={folders}
            mode="both"
            label="Category / Folder"
            market={(market as 'ANZ' | 'USA') || null}
            disabled={!market}
            placeholder={market ? 'All categories/folders' : 'Select a market first'}
            value={readCatFolder(params)}
            onChange={(v) => setParams(writeCatFolder(params, v))}
          />
        ),
        predicate: (a: FreshdeskSolutionArticle, params) => {
          const folder = params.get('folder')
          if (folder) return String(a.folder_id) === folder
          const category = params.get('category')
          if (category) return String(a.category_id) === category
          return true
        },
        isActive: (params) => !!(params.get('folder') || params.get('category')),
        clear: (params) => writeCatFolder(params, null),
      },
      columns: [
        { key: 'market', label: 'Market', sortable: true, render: (a: FreshdeskSolutionArticle) => marketChip(a.market) },
        { key: 'folder', label: 'Category / Folder', sortable: true, render: (a: FreshdeskSolutionArticle) => categoryFolder(a.category, a.folder) },
        { key: 'title', label: 'Title', sortable: true },
        { key: 'status', label: 'Status', sortable: true, render: (a: FreshdeskSolutionArticle) => statusChip(a.status) },
        { key: 'open', label: 'Open', align: 'right', render: (a: FreshdeskSolutionArticle) => openLink(a.url) },
      ],
    },
    {
      key: 'categories',
      label: 'Categories',
      icon: <AccountTreeOutlinedIcon fontSize="small" />,
      rows: categories,
      rowKey: (c: FreshdeskSolutionCategory) => `${c.category_id}-${c.market}`,
      defaultSortField: 'name',
      getMarket: (c: FreshdeskSolutionCategory) => c.market,
      searchText: (c: FreshdeskSolutionCategory) => c.name,
      searchPlaceholder: 'Search category',
      columns: [
        { key: 'market', label: 'Market', sortable: true, render: (c: FreshdeskSolutionCategory) => marketChip(c.market) },
        { key: 'name', label: 'Category', sortable: true },
        { key: 'folder_count', label: 'Folders', align: 'right', sortable: true, render: (c: FreshdeskSolutionCategory) => countLink(c.folder_count, `?view=folders&category=${c.category_id}`) },
        { key: 'article_count', label: 'Articles', align: 'right', sortable: true, render: (c: FreshdeskSolutionCategory) => countLink(c.article_count, `?view=articles&category=${c.category_id}`) },
      ],
    },
    {
      key: 'folders',
      label: 'Folders',
      icon: <FolderOutlinedIcon fontSize="small" />,
      rows: folders,
      rowKey: (f: FreshdeskSolutionFolder) => `${f.folder_id}-${f.market}`,
      defaultSortField: 'category',
      getMarket: (f: FreshdeskSolutionFolder) => f.market,
      searchText: (f: FreshdeskSolutionFolder) => `${f.name} ${f.category ?? ''}`,
      searchPlaceholder: 'Search folder or category',
      extraFilter: {
        render: (params, setParams, { market }) => (
          <CategoryFolderSelect
            categories={categories}
            folders={folders}
            mode="category"
            label="Category"
            market={(market as 'ANZ' | 'USA') || null}
            disabled={!market}
            placeholder={market ? 'All categories' : 'Select a market first'}
            value={
              params.get('category')
                ? { type: 'category', id: Number(params.get('category')) }
                : null
            }
            onChange={(v) => {
              const next = new URLSearchParams(params)
              next.delete('category')
              if (v) next.set('category', String(v.id))
              setParams(next)
            }}
          />
        ),
        predicate: (f: FreshdeskSolutionFolder, params) => {
          const category = params.get('category')
          if (!category) return true
          return String(f.category_id) === category
        },
        isActive: (params) => !!params.get('category'),
        clear: (params) => {
          const next = new URLSearchParams(params)
          next.delete('category')
          return next
        },
      },
      columns: [
        { key: 'market', label: 'Market', sortable: true, render: (f: FreshdeskSolutionFolder) => marketChip(f.market) },
        { key: 'category', label: 'Category', sortable: true, render: (f: FreshdeskSolutionFolder) => f.category || '-' },
        { key: 'name', label: 'Folder', sortable: true },
        { key: 'article_count', label: 'Articles', align: 'right', sortable: true, render: (f: FreshdeskSolutionFolder) => countLink(f.article_count, `?view=articles&folder=${f.folder_id}`) },
      ],
    },
  ]

  return (
    <IntegrationSectionTab
      title="Solutions knowledge base"
      titleIcon={<LayersOutlinedIcon />}
      runKey={KEY}
      syncLabel="Solutions"
      loading={loading && !data}
      entities={entities}
      onSynced={refetch}
    />
  )
}

export default FreshdeskSolutionsTab
